/**
 * useSidebarProjectDnD — drag-and-drop plumbing for sidebar projects.
 *
 * Owns the @dnd-kit sensors, the custom pointer/closest-corners collision
 * detector, and the drag-start/end/cancel handlers.  Also exposes the
 * `dragInProgressRef` + `suppressProjectClickAfterDragRef` refs so the
 * project row can gate click handlers.
 *
 * Extracted from `useSidebarOrchestration` without behaviour changes — the
 * handlers remain no-ops when `sidebarProjectSortOrder !== "manual"`.
 */

import { type RefObject, useCallback, useRef } from "react";
import {
  type CollisionDetection,
  type DragCancelEvent,
  type DragEndEvent,
  type DragStartEvent,
  PointerSensor,
  closestCorners,
  pointerWithin,
  type useSensors as UseSensorsType,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { scopedProjectKey } from "@cmmd-forge/client-runtime";
import type { SidebarProjectSortOrder } from "@cmmd-forge/contracts/settings";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";

// ---------------------------------------------------------------------------
// Input / return types
// ---------------------------------------------------------------------------

export interface SidebarProjectDnDInput {
  readonly sidebarProjects: readonly SidebarProjectSnapshot[];
  readonly sidebarProjectSortOrder: SidebarProjectSortOrder;
  readonly reorderProjects: (
    activeMemberKeys: readonly string[],
    overMemberKeys: readonly string[],
  ) => void;
}

export interface SidebarProjectDnDResult {
  readonly projectDnDSensors: ReturnType<typeof UseSensorsType>;
  readonly projectCollisionDetection: CollisionDetection;
  readonly dragInProgressRef: RefObject<boolean>;
  readonly suppressProjectClickAfterDragRef: RefObject<boolean>;
  readonly handleProjectDragStart: (event: DragStartEvent) => void;
  readonly handleProjectDragEnd: (event: DragEndEvent) => void;
  readonly handleProjectDragCancel: (event: DragCancelEvent) => void;
}

function readDragMemberKeys(data: {
  readonly current: Record<string, unknown> | undefined;
}): readonly string[] | null {
  const memberKeys = data.current?.sidebarProjectMemberKeys;
  return Array.isArray(memberKeys) && memberKeys.every((key) => typeof key === "string")
    ? memberKeys
    : null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSidebarProjectDnD(input: SidebarProjectDnDInput): SidebarProjectDnDResult {
  const { sidebarProjects, sidebarProjectSortOrder, reorderProjects } = input;

  const dragInProgressRef = useRef(false);
  const suppressProjectClickAfterDragRef = useRef(false);

  const projectDnDSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const projectCollisionDetection = useCallback<CollisionDetection>((args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return closestCorners(args);
  }, []);

  const handleProjectDragStart = useCallback(
    (_event: DragStartEvent) => {
      if (sidebarProjectSortOrder !== "manual") return;
      dragInProgressRef.current = true;
      suppressProjectClickAfterDragRef.current = true;
    },
    [sidebarProjectSortOrder],
  );

  const handleProjectDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (sidebarProjectSortOrder !== "manual") {
        dragInProgressRef.current = false;
        return;
      }
      dragInProgressRef.current = false;
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const activeProject = sidebarProjects.find((project) => project.projectKey === active.id);
      const overProject = sidebarProjects.find((project) => project.projectKey === over.id);
      const activeMemberKeys =
        readDragMemberKeys(active.data) ?? activeProject?.memberProjectRefs.map(scopedProjectKey);
      const overMemberKeys =
        readDragMemberKeys(over.data) ?? overProject?.memberProjectRefs.map(scopedProjectKey);
      if (!activeMemberKeys || !overMemberKeys) return;
      reorderProjects(activeMemberKeys, overMemberKeys);
    },
    [sidebarProjectSortOrder, reorderProjects, sidebarProjects],
  );

  const handleProjectDragCancel = useCallback((_event: DragCancelEvent) => {
    dragInProgressRef.current = false;
    // Clear the suppress flag so the NEXT click on any project row is not
    // silently swallowed.  handleProjectDragEnd already does this; a cancelled
    // drag (e.g. Escape) must do the same — otherwise the flag stays true for
    // the rest of the session and the first post-cancel project click is lost.
    suppressProjectClickAfterDragRef.current = false;
  }, []);

  return {
    projectDnDSensors,
    projectCollisionDetection,
    dragInProgressRef,
    suppressProjectClickAfterDragRef,
    handleProjectDragStart,
    handleProjectDragEnd,
    handleProjectDragCancel,
  };
}
